import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { useResolvedAppearance } from '@/db/hooks'
import { canDraw, studioModeOf, themeSpecToVars } from '@/lib/themeStudio'
import {
  CUSTOM_THEME,
  HORIZON_SURFACES,
  THEMES,
  THEME_SURFACES,
  THEME_DEFAULT_ACCENT,
  PRESETS,
  hueOfColour,
  paletteFromSurface,
  presetFor,
  randomPalette,
  surfaceFor,
  tokens,
  type Hue,
  type PaletteRecord,
} from '@/lib/colour'
import type { Look } from '@/lib/look'
import type { ThemeRef } from '@/lib/themeLayers'
import {
  HORIZON_TILE,
  OWN_TILE,
  themePick,
  tileOfRef,
  type SavedTile,
  type ThemeChoice,
  type ThemePick,
} from '@/lib/themePick'
import { ColourPicker } from '@/components/ColourPicker'
import { ThemeEditor } from '@/components/ThemeEditor'
import { Field, cx } from '@/components/ui'

type TileSurface = { ink: string; panel: string; raised: string; line: string; fg: string; muted: string }

// Horizon's tile is the night sky with a day card on it, as Auto always was.
const HORIZON_SURFACE: TileSurface = { ...HORIZON_SURFACES.night, panel: HORIZON_SURFACES.day.panel }

/** What a tile draws in when this build cannot draw a saved theme: the page's own surface. */
const CURRENT_SURFACE: TileSurface = {
  ink: 'var(--color-ink)',
  panel: 'var(--color-panel)',
  raised: 'var(--color-raised)',
  line: 'var(--color-line)',
  fg: 'var(--color-fg)',
  muted: 'var(--color-muted)',
}

/** A saved theme's tile surface: the theme as the Shell would draw it in this device's mode. */
const savedSurface = (spec: unknown, resolved: ReturnType<typeof useResolvedAppearance>) => {
  if (!canDraw(spec)) return undefined
  const v = themeSpecToVars(spec, resolved === 'workspace' ? 'dark' : studioModeOf(resolved)).vars
  const c = (k: string) => v[`--color-${k}`] ?? ''
  return {
    surface: {
      ink: c('ink'),
      panel: c('panel'),
      raised: c('raised'),
      line: c('line'),
      fg: c('fg'),
      muted: c('muted'),
    },
    accent: c('accent'),
  }
}

/** A tap as a Look, for a suite's Edit sheet: Horizon is no theme, a palette brings its accent. */
export const lookOfPick = (pick: ThemePick, keep?: Hue): Look =>
  pick.theme.kind === 'palette'
    ? {
        theme: pick.theme.theme,
        palette: pick.theme.theme === CUSTOM_THEME ? pick.theme.palette : undefined,
        colour: pick.colour,
      }
    : { theme: undefined, palette: undefined, colour: keep }

/**
 * The theme tiles and the accent under them, for one layer (docs/plans/theme-layers.md): Horizon,
 * every palette, the person's saved themes and, last, the way into the Themes studio. The same list
 * on every layer; the layer is the caller's. `value` is the theme this layer names (none = it passes
 * through, so no tile is marked). Controlled: `onPick` gets what a tap comes to (`themePick`), and
 * `onAccent` the hue picked under the tiles (omit it to hide the accent).
 */
export const ThemeTiles = ({
  value,
  colour: layerColour,
  fallbackHue,
  onPick,
  onAccent,
  saved = [],
  studio,
}: {
  value?: ThemeRef
  colour?: Hue
  /** the accent shown when nothing is set yet */
  fallbackHue: Hue
  onPick: (pick: ThemePick) => void
  onAccent?: (hue: Hue) => void
  /** the saved themes after the palettes (the Themes studio's) */
  saved?: SavedTile[]
  /** end the list with a tile into the Themes studio (`/themes`) */
  studio?: boolean
}) => {
  const [showPresets, setShowPresets] = useState(false)
  const [editing, setEditing] = useState(false)
  const palette = value?.kind === 'palette' ? value : undefined
  const custom = palette?.palette
  const colour = layerColour ?? fallbackHue
  const isCustom = palette?.theme === CUSTOM_THEME
  const preset = isCustom ? presetFor(custom) : undefined
  const cs = surfaceFor(CUSTOM_THEME, custom)
  const own = isCustom && !preset
  const resolved = useResolvedAppearance()
  const inForce = value ? tileOfRef(value, layerColour) : undefined
  const apply = (choice: ThemeChoice) => onPick(themePick(choice))
  const roll = () => {
    const { palette, accent } = randomPalette()
    apply({ own: palette, accent })
  }
  const pickOwn = (next: PaletteRecord) => {
    apply({ own: next, accent: colour })
    setEditing(false)
  }
  const shownPresets = PRESETS.filter((p) => showPresets || preset?.id === p.id)
  const tile = (
    key: string,
    testid: string,
    onClick: () => void,
    surface: TileSurface,
    accent: string,
    label: string,
    blurb: string,
    lead?: string,
    disabled?: boolean,
  ) => {
    const on = inForce === key
    return (
      <button
        key={testid}
        type="button"
        role="radio"
        aria-checked={on}
        disabled={disabled}
        onClick={onClick}
        className={cx(
          'rounded-xl p-2 text-left border transition-colors min-w-0',
          on ? 'border-accent ring-1 ring-accent' : 'border-line hover:border-line-strong',
        )}
        style={{ background: surface.ink, color: surface.fg }}
        data-testid={testid}
      >
        <div className="flex gap-1 mb-1.5">
          {lead && <span className="text-base leading-4">{lead}</span>}
          <span
            className="h-4 w-4 rounded"
            style={{ background: surface.panel, border: `1px solid ${surface.line}` }}
          />
          <span className="h-4 w-4 rounded" style={{ background: surface.raised }} />
          <span className="h-4 w-4 rounded-full" style={{ background: accent }} />
        </div>
        <div className="text-xs font-medium truncate">{label}</div>
        <div className="text-[10px] leading-tight" style={{ color: surface.muted }}>
          {blurb}
        </div>
      </button>
    )
  }
  // a saved theme: its emoji, name and swatch as it draws here; one this build cannot draw is shown, not worn
  const savedTileOf = (t: SavedTile) => {
    const drawn = savedSurface(t.spec, resolved)
    const name = t.profile?.spec.name ?? 'Your theme'
    const blurb = !t.profile
      ? 'no longer saved'
      : t.profile.spec.author_name && !t.mine
        ? `by ${t.profile.spec.author_name}`
        : 'your theme'
    return tile(
      t.key,
      `theme-saved-${t.profile?.id ?? 'worn'}`,
      () => apply({ saved: { profile: t.profile?.id, spec: t.profile?.spec.theme ?? t.spec } }),
      drawn?.surface ?? CURRENT_SURFACE,
      drawn?.accent ?? 'var(--color-accent)',
      name,
      drawn ? blurb : 'needs a newer app',
      t.profile?.spec.emoji ?? '🎨',
      !drawn,
    )
  }
  const tiles = (
    <div>
      <div
        className="grid grid-cols-3 gap-2"
        role="radiogroup"
        aria-label="Theme"
        data-testid="theme-tiles"
      >
        {tile(
          HORIZON_TILE,
          'theme-horizon',
          () => apply({ horizon: true }),
          HORIZON_SURFACE,
          tokens(colour).accent,
          'Horizon',
          'the default, light or dark',
        )}
        {THEMES.map((t) =>
          tile(
            t,
            `theme-${t}`,
            () => apply({ theme: t }),
            THEME_SURFACES[t],
            tokens(THEME_DEFAULT_ACCENT[t]).accent,
            THEME_SURFACES[t].label,
            THEME_SURFACES[t].blurb,
          ),
        )}
        {shownPresets.map((p) =>
          tile(
            p.id,
            `theme-${p.id}`,
            () => apply({ preset: p }),
            surfaceFor(CUSTOM_THEME, p.palette),
            preset?.id === p.id && custom?.accent ? custom.accent : (p.palette.accent ?? ''),
            p.label,
            p.blurb,
          ),
        )}
        {/* the dice: a new palette every tap, kept as this scope's own */}
        <button
          type="button"
          role="radio"
          aria-checked={inForce === OWN_TILE}
          onClick={roll}
          className={cx(
            'rounded-xl p-2 text-left border transition-colors min-w-0',
            inForce === OWN_TILE
              ? 'border-accent ring-1 ring-accent'
              : 'border-line hover:border-line-strong',
          )}
          style={own && custom ? { background: cs.ink, color: cs.fg } : undefined}
          data-testid="theme-random"
        >
          <div className="flex gap-1 mb-1.5">
            {own && custom ? (
              <>
                <span
                  className="h-4 w-4 rounded"
                  style={{ background: cs.panel, border: `1px solid ${cs.line}` }}
                />
                <span className="h-4 w-4 rounded" style={{ background: cs.raised }} />
                <span
                  className="h-4 w-4 rounded-full"
                  style={{ background: custom.accent ?? tokens(colour).accent }}
                />
              </>
            ) : (
              <span className="text-base leading-4">🎲</span>
            )}
          </div>
          <div className="text-xs font-medium">{own ? 'Yours' : 'Random'}</div>
          <div className="text-[10px] leading-tight" style={own && custom ? { color: cs.muted } : undefined}>
            {own ? 'tap again to reroll' : 'roll until one clicks'}
          </div>
        </button>
        {saved.map((t) => savedTileOf(t))}
        {studio && (
          <Link
            to="/themes"
            className="rounded-xl p-2 text-left border border-line hover:border-line-strong min-w-0"
            data-testid="open-themes-studio"
          >
            <div className="flex gap-1 mb-1.5">
              <span className="text-base leading-4">🎨</span>
            </div>
            <div className="text-xs font-medium">Themes studio</div>
            <div className="text-[10px] leading-tight text-muted">make your own, see everyone’s</div>
          </Link>
        )}
      </div>
      <div className="mt-1.5 flex items-center gap-3">
        <button
          type="button"
          onClick={() => setShowPresets((v) => !v)}
          className="text-xs text-muted hover:text-fg min-h-9"
          data-testid="theme-more"
        >
          {showPresets ? 'Fewer palettes' : `${PRESETS.length} more palettes (Catppuccin)…`}
        </button>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="text-xs text-muted hover:text-fg min-h-9"
          data-testid="theme-edit"
        >
          Edit colours…
        </button>
      </div>
      {preset && (
        <div className="mt-3">
          <div className="text-[13px] font-semibold text-muted mb-1.5">{preset.label} accent</div>
          <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Preset accent">
            {preset.accents.map((a) => {
              const on = custom?.accent === a.hex
              return (
                <button
                  key={a.name}
                  type="button"
                  role="radio"
                  aria-checked={on}
                  aria-label={a.name}
                  title={a.name}
                  onClick={() =>
                    onPick({
                      theme: { kind: 'palette', theme: CUSTOM_THEME, palette: { ...custom, accent: a.hex } },
                      colour: hueOfColour(a.hex) ?? colour,
                    })
                  }
                  className={cx(
                    'h-8 w-8 rounded-full border-2 transition-transform active:scale-95',
                    on ? 'border-fg scale-110' : 'border-transparent',
                  )}
                  style={{ background: a.hex }}
                  data-testid={`preset-accent-${a.name}`}
                />
              )
            })}
          </div>
        </div>
      )}
      {editing && (
        <ThemeEditor
          value={
            custom && isCustom
              ? paletteFromSurface(cs, custom.accent ?? tokens(colour).accent)
              : paletteFromSurface(surfaceFor(palette?.theme, undefined), tokens(colour).accent)
          }
          onUse={pickOwn}
          onClose={() => setEditing(false)}
        />
      )}
    </div>
  )
  return (
    <>
      <div className="mb-3">{tiles}</div>
      {onAccent && !(isCustom && custom?.accent) && (
        <Field label="Accent">
          <ColourPicker value={colour} onChange={(c) => onAccent(c as Hue)} />
        </Field>
      )}
    </>
  )
}

/**
 * Palette and accent for a suite's Edit sheet: the workspace layer's theme and accent, written as
 * the three fields a Workspace carries (`theme`, `palette`, `colour`). Controlled: `onChange` gets
 * the whole next look.
 */
export const LookPicker = ({
  value,
  fallbackHue,
  onChange,
}: {
  value: Look
  fallbackHue: Hue
  onChange: (next: Look) => void
}) => {
  const ref: ThemeRef = value.theme
    ? { kind: 'palette', theme: value.theme, palette: value.palette }
    : { kind: 'horizon' }
  return (
    <Field label="Theme" hint="The whole surface: background, cards, text. Accent below sits on top.">
      <ThemeTiles
        value={ref}
        colour={value.colour}
        fallbackHue={fallbackHue}
        onPick={(pick) => onChange(lookOfPick(pick, value.colour))}
        onAccent={(c) => onChange({ ...value, colour: c })}
      />
    </Field>
  )
}
